/**
 * "Nest when idle" — Koko flies to a corner and perches after N
 * minutes of no user activity. Mirrors `KokoNest.swift`.
 *
 * Parameters (updated per 2026-07-04 yap):
 *   - idle threshold default 30 s, minimum 30 s (settings: `nestIdleSeconds`)
 *   - legacy `koko_nest_idle_minutes` localStorage entry takes
 *     precedence when present (test escape hatch + back-compat)
 *   - corner inset 60 pt
 *   - picks the corner furthest from the current cursor so it isn't
 *     in the user's way
 *   - transitions to `perched` on arrival
 *
 * Reset triggers (from `CompanionManager.swift:1301, 1495`):
 *   - mousemove
 *   - user submits a typed or voice turn
 *   - wake word detected
 *   - hotkey pressed
 *
 * On wake-up the idle timer simply starts again — the bird's existing
 * cursor-follow spring will yank it back toward the cursor naturally.
 */

import type { SettingsStore } from "../app/SettingsStore";
import { SPRITE_SIZE_PX, type MovementController } from "./MovementController";
import { clampPerchTargetToChrome } from "./perchExclusion";

const CORNER_INSET_PX = 60;
/** Hard floor for the user-facing idle threshold (yap 2026-07-04). */
export const MIN_NEST_IDLE_SECONDS = 30;
/**
 * While perched, poll the fixed chrome at this cadence and re-clamp the
 * perch if the chrome moved under the bird (e.g. the studio timeline
 * dock expanding mid-perch — yap 2026-07-07). Polling only runs while
 * nested, so it costs nothing in normal cursor-follow, and it is the
 * cheapest mechanism that survives the dock APPEARING later, resizing,
 * or being re-created (a ResizeObserver would need re-attachment
 * plumbing for all three).
 */
const NEST_RECLAMP_POLL_MS = 500;
/** Ignore sub-pixel clamp jitter when deciding whether to re-fly. */
const NEST_RECLAMP_EPSILON_PX = 1;

/** localStorage read that tolerates privacy-mode Safari / SSR. */
function readRawLocalStorage(key: string): string | null {
  if (typeof window === "undefined" || !window.localStorage) return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

export interface NestControllerOptions {
  settings: SettingsStore;
  movementController: MovementController;
  getLastCursorPosition: () => { x: number; y: number };
  /**
   * Optional host-supplied perch targets in viewport coordinates. When
   * this returns a non-empty list (e.g. the studio's procedural branch
   * tips), the bird perches on the target furthest from the cursor
   * instead of a bare screen corner. Null/undefined means the host has
   * no perch system and preserves corner nesting. An empty list means a
   * host exists but currently has no safe perch, so nesting is skipped.
   * Re-queried on every nest so the host can reflect layout changes.
   */
  getPerchPoints?: () =>
    | { viewportX: number; viewportY: number }[]
    | null
    | undefined;
}

export class NestController {
  private readonly options: NestControllerOptions;
  private timerId: number | null = null;
  private reclampTimerId: number | null = null;
  private isRunning = false;
  private isCurrentlyNested = false;
  /** Where the bird actually sits — the DESIRED target after chrome clamping. */
  private currentNestTarget: { viewportX: number; viewportY: number } | null = null;
  /**
   * The pre-clamp target (branch tip / corner) the bird WANTS. Kept
   * separately so the re-clamp poll can settle the bird back down when
   * the chrome that displaced it shrinks again (dock collapse).
   */
  private desiredNestTarget: { viewportX: number; viewportY: number } | null = null;

  constructor(options: NestControllerOptions) {
    this.options = options;

    // React to user changes — if they flip the toggle off mid-idle,
    // we stop the timer immediately so the bird doesn't surprise them.
    options.settings.subscribe("nestEnabled", () => this.rearm());
    options.settings.subscribe("nestIdleMinutes", () => this.rearm());
    options.settings.subscribe("nestIdleSeconds", () => this.rearm());
    options.settings.subscribe("sessionActive", () => this.rearm());
  }

  start(): void {
    this.isRunning = true;
    this.rearm();
  }

  stop(): void {
    this.isRunning = false;
    this.clearTimer();
    this.stopReclampPoll();
  }

  /**
   * Call from any user activity — cursor motion, typed input, push-to-
   * talk, wake word, hotkey. Wakes the bird if it's nested AND restarts
   * the idle countdown so it doesn't perch again immediately. Matches
   * native `CompanionManager.swift:1301, 1495` behavior exactly.
   */
  resetIdleTimer(): void {
    if (!this.isRunning) return;
    if (this.isCurrentlyNested) {
      this.wakeFromNest();
    }
    this.rearm();
  }

  /** Whether the bird is currently perched at a nest corner. */
  isNested(): boolean {
    return this.isCurrentlyNested;
  }

  /**
   * Returns the active nest corner position, or null if the bird is
   * not currently nested. Pointing flights use this to remember where
   * to fly back to after the 3.5s hold completes — without it the bird
   * would drift back to the cursor and the nest state would silently
   * break, mirroring the bug in `runProactiveObservation` before this
   * fix landed.
   */
  getCurrentNestTarget(): { viewportX: number; viewportY: number } | null {
    return this.isCurrentlyNested ? this.currentNestTarget : null;
  }

  /**
   * Fly back to the saved nest corner without resetting the idle
   * countdown. Used by `KokoApp.runPointing` after the bird has flown
   * out from the nest to point at a target — once the hold completes
   * the bird should re-perch at the same corner instead of drifting
   * back to the cursor.
   */
  async returnToNestTarget(
    target: { viewportX: number; viewportY: number },
  ): Promise<void> {
    // Re-clamp on the way back: the dock may have expanded while the
    // bird was out pointing, swallowing the saved perch.
    const clamped = clampPerchTargetToChrome(target, SPRITE_SIZE_PX);
    this.isCurrentlyNested = true;
    this.desiredNestTarget = target;
    this.currentNestTarget = clamped;
    this.beginReclampPoll();
    await this.options.movementController.flyToNestTarget(clamped);
  }

  private rearm(): void {
    this.clearTimer();
    if (!this.isRunning) return;
    if (!this.options.settings.get("sessionActive")) return;
    if (!this.options.settings.get("nestEnabled")) return;

    const idleMs = this.resolveIdleMs();
    this.timerId = window.setTimeout(() => {
      this.timerId = null;
      void this.nestNow();
    }, idleMs);
  }

  /**
   * Effective idle threshold in ms.
   *
   * Product rule (2026-07-04 yap): minimum 30 seconds, default 30
   * seconds — enforced on `nestIdleSeconds`.
   *
   * Back-compat/test escape hatch: when a RAW `koko_nest_idle_minutes`
   * localStorage entry exists (older persisted settings, or test
   * suites that seed sub-minute values to force nesting in ~1 s), it
   * wins verbatim. New UI writes only `nestIdleSeconds`.
   */
  private resolveIdleMs(): number {
    const legacyRaw = readRawLocalStorage("koko_nest_idle_minutes");
    if (legacyRaw !== null) {
      let legacyMinutes = Number.NaN;
      try {
        legacyMinutes = Number(JSON.parse(legacyRaw));
      } catch {
        // Corrupt entry — ignore and fall through to nestIdleSeconds.
      }
      if (Number.isFinite(legacyMinutes) && legacyMinutes > 0) {
        return legacyMinutes * 60 * 1000;
      }
    }
    const idleSeconds = Math.max(
      MIN_NEST_IDLE_SECONDS,
      this.options.settings.get("nestIdleSeconds"),
    );
    return idleSeconds * 1000;
  }

  private async nestNow(): Promise<void> {
    if (this.isCurrentlyNested) return;
    const desired = this.pickNestTarget();
    if (!desired) {
      this.rearm();
      return;
    }
    // Perch discipline (yap 2026-07-07): never settle on fixed chrome —
    // a target inside the timeline dock / top strip is clamped clear.
    const target = clampPerchTargetToChrome(desired, SPRITE_SIZE_PX);
    this.isCurrentlyNested = true;
    this.desiredNestTarget = desired;
    this.currentNestTarget = target;
    this.beginReclampPoll();
    await this.options.movementController.flyToNestTarget(target);
  }

  /**
   * Choose where to perch. Host-supplied perch points (branch tips) win
   * when present; otherwise fall back to the furthest screen corner only
   * when the host does not provide a perch system. An explicit empty list
   * means the host has no currently safe resting place.
   */
  private pickNestTarget(): { viewportX: number; viewportY: number } | null {
    const perchPoints = this.options.getPerchPoints?.();
    if (perchPoints === null || perchPoints === undefined) {
      return this.pickCornerFurthestFromCursor();
    }
    if (perchPoints.length > 0) {
      return this.pickFurthestFromCursor(perchPoints);
    }
    return null;
  }

  /**
   * While nested, watch the fixed chrome and re-fly the bird when its
   * perch stops being legal — the dock expanded under it — or when the
   * chrome that displaced it shrank back (re-clamp of the DESIRED
   * target, not the current one, so the bird settles back down after a
   * dock collapse). No-op unless the bird is actually sitting at the
   * nest: pointing flights and the nest flight itself own the position.
   */
  private beginReclampPoll(): void {
    if (this.reclampTimerId !== null) return;
    this.reclampTimerId = window.setInterval(() => this.reclampTick(), NEST_RECLAMP_POLL_MS);
  }

  private stopReclampPoll(): void {
    if (this.reclampTimerId !== null) {
      window.clearInterval(this.reclampTimerId);
      this.reclampTimerId = null;
    }
  }

  private reclampTick(): void {
    if (!this.isCurrentlyNested || !this.desiredNestTarget || !this.currentNestTarget) {
      return;
    }
    if (!this.options.movementController.isPerchedAtNest()) return;
    const livePerches = this.options.getPerchPoints?.();
    if (Array.isArray(livePerches)) {
      const desiredIsStillSafe = livePerches.some(
        (point) =>
          Math.hypot(
            point.viewportX - this.desiredNestTarget!.viewportX,
            point.viewportY - this.desiredNestTarget!.viewportY,
          ) < NEST_RECLAMP_EPSILON_PX,
      );
      if (!desiredIsStillSafe) {
        if (livePerches.length === 0) {
          this.wakeFromNest();
          this.rearm();
          return;
        }
        this.desiredNestTarget = this.pickFurthestFromCursor(livePerches);
      }
    }
    const clamped = clampPerchTargetToChrome(this.desiredNestTarget, SPRITE_SIZE_PX);
    const drift = Math.hypot(
      clamped.viewportX - this.currentNestTarget.viewportX,
      clamped.viewportY - this.currentNestTarget.viewportY,
    );
    if (drift < NEST_RECLAMP_EPSILON_PX) return;
    this.currentNestTarget = clamped;
    void this.options.movementController.flyToNestTarget(clamped);
  }

  private pickFurthestFromCursor<T extends { viewportX: number; viewportY: number }>(
    points: T[],
  ): T {
    const { x: cursorX, y: cursorY } = this.options.getLastCursorPosition();
    let furthest = points[0];
    let furthestDistance = -1;
    for (const point of points) {
      const dx = point.viewportX - cursorX;
      const dy = point.viewportY - cursorY;
      const distance = dx * dx + dy * dy;
      if (distance > furthestDistance) {
        furthestDistance = distance;
        furthest = point;
      }
    }
    return furthest;
  }

  private wakeFromNest(): void {
    this.isCurrentlyNested = false;
    this.currentNestTarget = null;
    this.desiredNestTarget = null;
    this.stopReclampPoll();
    this.options.movementController.leaveNest();
  }

  private clearTimer(): void {
    if (this.timerId !== null) {
      window.clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  private pickCornerFurthestFromCursor(): { viewportX: number; viewportY: number } {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    return this.pickFurthestFromCursor([
      { viewportX: CORNER_INSET_PX, viewportY: CORNER_INSET_PX },
      { viewportX: viewportWidth - CORNER_INSET_PX, viewportY: CORNER_INSET_PX },
      { viewportX: CORNER_INSET_PX, viewportY: viewportHeight - CORNER_INSET_PX },
      {
        viewportX: viewportWidth - CORNER_INSET_PX,
        viewportY: viewportHeight - CORNER_INSET_PX,
      },
    ]);
  }
}
